import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Video, Calendar, Clock, User, Users, Phone, Search,
  Plus, X, Check, Loader2, ExternalLink, Copy,
  AlertCircle, CheckCircle, Play, VideoOff, Edit2, XCircle,
  MessageSquare, Bell, Mail
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VideoConsultManager = ({ isDoctor = false, currentUser = null }) => {
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(null);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [patientSearch, setPatientSearch] = useState('');
  const [loadingPatients, setLoadingPatients] = useState(false);
  
  // Per la risposta del paziente
  const [responseData, setResponseData] = useState({
    action: '',
    proposed_date: '',
    proposed_time: '',
    message: ''
  });
  
  const [formData, setFormData] = useState({
    patient_id: '',
    patient_name: '',
    doctor_id: currentUser?.id || '',
    scheduled_date: '',
    scheduled_time: '',
    duration_minutes: 30,
    notes: '',
    reason: ''
  });

  useEffect(() => {
    loadConsultations();
    if (isDoctor) {
      loadPatients();
    } else {
      loadDoctors();
    }
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const loadConsultations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/videoconsult/list`, getAuthHeaders());
      setConsultations(response.data.consultations || []);
    } catch (error) {
      console.error('Error loading consultations:', error);
      setConsultations([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPatients = async (search = '') => {
    setLoadingPatients(true);
    try {
      const endpoint = search 
        ? `${API}/patients?search=${encodeURIComponent(search)}`
        : `${API}/patients`;
      const response = await axios.get(endpoint, getAuthHeaders());
      setPatients(response.data.patients || response.data || []);
    } catch (error) {
      console.error('Error loading patients:', error);
    } finally {
      setLoadingPatients(false);
    }
  };

  const loadDoctors = async () => {
    try {
      const response = await axios.get(`${API}/doctors`, getAuthHeaders());
      setDoctors(response.data.doctors || response.data || []);
    } catch (error) {
      console.error('Error loading doctors:', error);
    }
  };

  // Ricerca pazienti con debounce
  useEffect(() => {
    if (isDoctor && patientSearch.length >= 2) {
      const timer = setTimeout(() => {
        loadPatients(patientSearch);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [patientSearch, isDoctor]);

  const handleSchedule = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const scheduledAt = `${formData.scheduled_date}T${formData.scheduled_time}:00`;
      
      await axios.post(`${API}/videoconsult/schedule`, {
        patient_id: formData.patient_id,
        doctor_id: isDoctor ? currentUser.id : formData.doctor_id,
        scheduled_at: scheduledAt,
        duration_minutes: formData.duration_minutes,
        reason: formData.reason,
        notes: formData.notes
      }, getAuthHeaders());
      
      toast.success('Videoconsulto programmato! Il paziente riceverà una notifica.');
      setShowScheduleModal(false);
      resetForm();
      loadConsultations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella programmazione');
    } finally {
      setSaving(false);
    }
  };

  // Risposta del paziente (accetta/modifica/rifiuta)
  const handlePatientResponse = async (consultId, action) => {
    setSaving(true);
    try {
      let payload = { action };
      
      if (action === 'propose_change' && responseData.proposed_date && responseData.proposed_time) {
        payload.proposed_date = `${responseData.proposed_date}T${responseData.proposed_time}:00`;
        payload.message = responseData.message;
      }
      if (action === 'decline') {
        payload.message = responseData.message;
      }
      
      await axios.post(`${API}/videoconsult/${consultId}/respond`, payload, getAuthHeaders());
      
      const messages = {
        accept: 'Videoconsulto confermato! Il medico riceverà notifica.',
        propose_change: 'Proposta di modifica inviata al medico.',
        decline: 'Videoconsulto rifiutato. Il medico è stato notificato.'
      };
      
      toast.success(messages[action]);
      setShowResponseModal(null);
      setResponseData({ action: '', proposed_date: '', proposed_time: '', message: '' });
      loadConsultations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella risposta');
    } finally {
      setSaving(false);
    }
  };

  // Medico accetta/rifiuta proposta di modifica
  const handleDoctorResponse = async (consultId, action) => {
    setSaving(true);
    try {
      await axios.post(`${API}/videoconsult/${consultId}/doctor-respond`, { action }, getAuthHeaders());
      
      const messages = {
        accept_change: 'Nuova data accettata! Il paziente sarà notificato.',
        decline_change: 'Proposta rifiutata. Il paziente sarà notificato.'
      };
      
      toast.success(messages[action]);
      loadConsultations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    } finally {
      setSaving(false);
    }
  };

  const joinCall = async (consultId) => {
    try {
      const response = await axios.post(`${API}/videoconsult/${consultId}/join`, {}, getAuthHeaders());
      const { jitsi_url, room_name } = response.data;
      
      setActiveCall(consultId);
      window.open(jitsi_url, '_blank', 'width=1200,height=800');
      toast.success('Videoconsulto avviato! Si aprirà in una nuova finestra.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore avvio videoconsulto');
    }
  };

  const endCall = async (consultId) => {
    try {
      await axios.post(`${API}/videoconsult/${consultId}/end`, {}, getAuthHeaders());
      setActiveCall(null);
      toast.success('Videoconsulto terminato');
      loadConsultations();
    } catch (error) {
      toast.error('Errore terminazione videoconsulto');
    }
  };

  const cancelConsult = async (consultId) => {
    if (!window.confirm('Sei sicuro di voler cancellare questo videoconsulto?')) return;
    
    try {
      await axios.delete(`${API}/videoconsult/${consultId}`, getAuthHeaders());
      toast.success('Videoconsulto cancellato');
      loadConsultations();
    } catch (error) {
      toast.error('Errore cancellazione');
    }
  };

  const resetForm = () => {
    setFormData({
      patient_id: '',
      patient_name: '',
      doctor_id: currentUser?.id || '',
      scheduled_date: '',
      scheduled_time: '',
      duration_minutes: 30,
      notes: '',
      reason: ''
    });
    setPatientSearch('');
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: '⏳ In attesa di conferma' },
      confirmed: { bg: 'bg-green-100', text: 'text-green-700', label: '✓ Confermato' },
      change_proposed: { bg: 'bg-blue-100', text: 'text-blue-700', label: '📝 Modifica proposta' },
      declined: { bg: 'bg-red-100', text: 'text-red-700', label: '✗ Rifiutato' },
      completed: { bg: 'bg-gray-100', text: 'text-gray-700', label: '✓ Completato' },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Cancellato' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  // Filtra consultazioni
  const upcomingConsultations = useMemo(() => 
    consultations.filter(c => new Date(c.scheduled_at) >= new Date() && !['cancelled', 'declined', 'completed'].includes(c.status))
      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)),
    [consultations]
  );

  const pastConsultations = useMemo(() =>
    consultations.filter(c => new Date(c.scheduled_at) < new Date() || ['cancelled', 'declined', 'completed'].includes(c.status))
      .sort((a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at)),
    [consultations]
  );

  // Filtro pazienti per ricerca
  const filteredPatients = useMemo(() => {
    if (!patientSearch) return patients;
    const search = patientSearch.toLowerCase();
    return patients.filter(p => 
      `${p.first_name} ${p.last_name}`.toLowerCase().includes(search) ||
      p.email?.toLowerCase().includes(search) ||
      p.phone?.includes(search) ||
      p.codice_fiscale?.toLowerCase().includes(search)
    );
  }, [patients, patientSearch]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#2A9FCB]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#2A9FCB] flex items-center gap-2">
            <Video className="w-7 h-7" />
            Videoconsulti
          </h2>
          <p className="text-[#2A9FCB]/70 mt-1">
            Gestisci le tue consulenze video con {isDoctor ? 'i pazienti' : 'i medici'}
          </p>
        </div>
        <button
          onClick={() => setShowScheduleModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#2A9FCB] text-white rounded-lg hover:bg-[#2387b3] transition-all"
          data-testid="schedule-videoconsult-btn"
        >
          <Plus className="w-5 h-5" />
          Programma Videoconsulto
        </button>
      </div>

      {/* Active Call Banner */}
      {activeCall && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center animate-pulse">
              <Video className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-semibold text-green-800">Videoconsulto in corso</p>
              <p className="text-sm text-green-600">La chiamata è attiva in un'altra finestra</p>
            </div>
          </div>
          <button
            onClick={() => endCall(activeCall)}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-2"
          >
            <VideoOff className="w-4 h-4" />
            Termina
          </button>
        </div>
      )}

      {/* Upcoming Consultations */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-[#2A9FCB]/10">
        <h3 className="font-semibold text-[#2A9FCB] mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Prossimi Videoconsulti ({upcomingConsultations.length})
        </h3>
        
        {upcomingConsultations.length === 0 ? (
          <div className="text-center py-8 text-[#2A9FCB]/60">
            <Video className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>Nessun videoconsulto programmato</p>
            <button 
              onClick={() => setShowScheduleModal(true)}
              className="mt-3 text-[#2A9FCB] hover:underline text-sm"
            >
              Programma il primo videoconsulto →
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {upcomingConsultations.map(consult => (
              <div key={consult.id} className="border rounded-lg p-4 hover:shadow-md transition-all">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {getStatusBadge(consult.status)}
                      <span className="text-sm text-gray-500">
                        {formatDate(consult.scheduled_at)}
                      </span>
                    </div>
                    <p className="font-medium text-gray-900">
                      {isDoctor ? (
                        <>Paziente: {consult.patient_name}</>
                      ) : (
                        <>Medico: {consult.doctor_name}</>
                      )}
                    </p>
                    {consult.reason && (
                      <p className="text-sm text-gray-500 mt-1">Motivo: {consult.reason}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      Durata prevista: {consult.duration_minutes || 30} minuti
                    </p>
                    
                    {/* Mostra proposta di modifica se esiste */}
                    {consult.status === 'change_proposed' && consult.proposed_date && (
                      <div className="mt-2 p-2 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-700">
                          <strong>Nuova data proposta:</strong> {formatDate(consult.proposed_date)}
                        </p>
                        {consult.change_message && (
                          <p className="text-xs text-blue-600 mt-1">Messaggio: {consult.change_message}</p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2 flex-wrap">
                    {/* Pulsanti per il PAZIENTE - rispondere a richieste pending */}
                    {!isDoctor && consult.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handlePatientResponse(consult.id, 'accept')}
                          className="px-3 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm flex items-center gap-1"
                        >
                          <Check className="w-4 h-4" />
                          Accetta
                        </button>
                        <button
                          onClick={() => setShowResponseModal(consult)}
                          className="px-3 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm flex items-center gap-1"
                        >
                          <Edit2 className="w-4 h-4" />
                          Modifica
                        </button>
                        <button
                          onClick={() => {
                            setResponseData({ ...responseData, action: 'decline' });
                            setShowResponseModal(consult);
                          }}
                          className="px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm flex items-center gap-1"
                        >
                          <XCircle className="w-4 h-4" />
                          Rifiuta
                        </button>
                      </>
                    )}
                    
                    {/* Pulsanti per il MEDICO - rispondere a proposte di modifica */}
                    {isDoctor && consult.status === 'change_proposed' && (
                      <>
                        <button
                          onClick={() => handleDoctorResponse(consult.id, 'accept_change')}
                          className="px-3 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm flex items-center gap-1"
                        >
                          <Check className="w-4 h-4" />
                          Accetta nuova data
                        </button>
                        <button
                          onClick={() => handleDoctorResponse(consult.id, 'decline_change')}
                          className="px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm flex items-center gap-1"
                        >
                          <XCircle className="w-4 h-4" />
                          Rifiuta
                        </button>
                      </>
                    )}
                    
                    {/* Pulsante Avvia (entro 15 min) */}
                    {consult.status === 'confirmed' && new Date(consult.scheduled_at) <= new Date(Date.now() + 15 * 60 * 1000) && (
                      <button
                        onClick={() => joinCall(consult.id)}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2"
                      >
                        <Play className="w-4 h-4" />
                        Avvia
                      </button>
                    )}
                    
                    {/* Pulsante Cancella */}
                    {['pending', 'confirmed'].includes(consult.status) && (
                      <button
                        onClick={() => cancelConsult(consult.id)}
                        className="px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg text-sm"
                      >
                        Cancella
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Past Consultations */}
      {pastConsultations.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="font-semibold text-gray-600 mb-4">
            Videoconsulti Passati ({pastConsultations.length})
          </h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {pastConsultations.slice(0, 10).map(consult => (
              <div key={consult.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-700 text-sm">
                    {isDoctor ? consult.patient_name : consult.doctor_name}
                  </p>
                  <p className="text-xs text-gray-500">{formatDate(consult.scheduled_at)}</p>
                </div>
                {getStatusBadge(consult.status)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between bg-gradient-to-r from-[#2A9FCB] to-[#3db8e8]">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Video className="w-6 h-6" />
                Programma Videoconsulto
              </h3>
              <button 
                onClick={() => { setShowScheduleModal(false); resetForm(); }} 
                className="text-white/80 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleSchedule} className="p-6 space-y-4">
              {/* Selezione Paziente (per medici) con ricerca */}
              {isDoctor ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <User className="w-4 h-4 inline mr-1" />
                    Cerca Paziente *
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={patientSearch}
                      onChange={(e) => setPatientSearch(e.target.value)}
                      placeholder="Cerca per nome, email, telefono, CF..."
                      className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]"
                      data-testid="patient-search-input"
                    />
                    {loadingPatients && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-[#2A9FCB]" />
                    )}
                  </div>
                  
                  {/* Lista pazienti filtrati */}
                  {(patientSearch || filteredPatients.length > 0) && (
                    <div className="mt-2 border rounded-lg max-h-48 overflow-y-auto">
                      {filteredPatients.length === 0 ? (
                        <p className="p-3 text-gray-500 text-sm text-center">
                          {patientSearch ? 'Nessun paziente trovato' : 'Inizia a digitare per cercare'}
                        </p>
                      ) : (
                        filteredPatients.slice(0, 20).map(p => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => {
                              setFormData({...formData, patient_id: p.id, patient_name: `${p.first_name} ${p.last_name}`});
                              setPatientSearch(`${p.first_name} ${p.last_name}`);
                            }}
                            className={`w-full text-left p-3 hover:bg-[#2A9FCB]/5 border-b last:border-b-0 ${
                              formData.patient_id === p.id ? 'bg-[#2A9FCB]/10' : ''
                            }`}
                            data-testid={`patient-option-${p.id}`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">{p.first_name} {p.last_name}</p>
                                <p className="text-xs text-gray-500">{p.email}</p>
                              </div>
                              {formData.patient_id === p.id && (
                                <Check className="w-5 h-5 text-[#2A9FCB]" />
                              )}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                  
                  {formData.patient_id && (
                    <p className="text-sm text-green-600 mt-1">
                      ✓ Paziente selezionato: {formData.patient_name}
                    </p>
                  )}
                </div>
              ) : (
                /* Selezione Medico (per pazienti) */
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Medico *
                  </label>
                  <select
                    value={formData.doctor_id}
                    onChange={(e) => setFormData({...formData, doctor_id: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]"
                    required
                  >
                    <option value="">Seleziona medico...</option>
                    {doctors.map(d => (
                      <option key={d.id} value={d.id}>
                        Dr. {d.first_name} {d.last_name} - {d.specialization || 'Oculista'}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Data e Ora */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Data *
                  </label>
                  <input
                    type="date"
                    value={formData.scheduled_date}
                    onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Ora *
                  </label>
                  <input
                    type="time"
                    value={formData.scheduled_time}
                    onChange={(e) => setFormData({...formData, scheduled_time: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]"
                    required
                  />
                </div>
              </div>

              {/* Durata */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Durata (minuti)
                </label>
                <select
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]"
                >
                  <option value={15}>15 minuti</option>
                  <option value={30}>30 minuti</option>
                  <option value={45}>45 minuti</option>
                  <option value={60}>60 minuti</option>
                </select>
              </div>

              {/* Motivo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motivo della consulenza
                </label>
                <input
                  type="text"
                  value={formData.reason}
                  onChange={(e) => setFormData({...formData, reason: e.target.value})}
                  placeholder="Es: Controllo post-operatorio, Consulto per..."
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]"
                />
              </div>

              {/* Note */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note aggiuntive
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Informazioni utili per la consulenza..."
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]"
                />
              </div>

              {/* Info notifiche */}
              <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-700">
                <div className="flex items-start gap-2">
                  <Bell className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Il paziente riceverà:</p>
                    <ul className="mt-1 space-y-1 text-xs">
                      <li>📧 Email di notifica</li>
                      <li>📱 SMS (se abilitato)</li>
                      <li>🔔 Notifica in-app</li>
                    </ul>
                    <p className="mt-2 text-xs">Potrà accettare, proporre una modifica o rifiutare.</p>
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowScheduleModal(false); resetForm(); }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={saving || (isDoctor && !formData.patient_id) || (!isDoctor && !formData.doctor_id)}
                  className="flex-1 px-4 py-2 bg-[#2A9FCB] text-white rounded-lg hover:bg-[#2387b3] disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
                  Programma
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Response Modal (per paziente) */}
      {showResponseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6 border-b">
              <h3 className="text-xl font-bold text-[#2A9FCB]">
                {responseData.action === 'decline' ? 'Rifiuta Videoconsulto' : 'Proponi Nuova Data'}
              </h3>
            </div>
            
            <div className="p-6 space-y-4">
              {responseData.action !== 'decline' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nuova Data Proposta *
                    </label>
                    <input
                      type="date"
                      value={responseData.proposed_date}
                      onChange={(e) => setResponseData({...responseData, proposed_date: e.target.value})}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nuova Ora Proposta *
                    </label>
                    <input
                      type="time"
                      value={responseData.proposed_time}
                      onChange={(e) => setResponseData({...responseData, proposed_time: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    />
                  </div>
                </>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Messaggio {responseData.action === 'decline' ? '*' : '(opzionale)'}
                </label>
                <textarea
                  value={responseData.message}
                  onChange={(e) => setResponseData({...responseData, message: e.target.value})}
                  placeholder={responseData.action === 'decline' ? 'Motivo del rifiuto...' : 'Aggiungi un messaggio...'}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg"
                  required={responseData.action === 'decline'}
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowResponseModal(null);
                    setResponseData({ action: '', proposed_date: '', proposed_time: '', message: '' });
                  }}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Annulla
                </button>
                <button
                  onClick={() => handlePatientResponse(
                    showResponseModal.id, 
                    responseData.action === 'decline' ? 'decline' : 'propose_change'
                  )}
                  disabled={saving || (responseData.action !== 'decline' && (!responseData.proposed_date || !responseData.proposed_time))}
                  className={`flex-1 px-4 py-2 text-white rounded-lg disabled:opacity-50 ${
                    responseData.action === 'decline' ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-500 hover:bg-blue-600'
                  }`}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (
                    responseData.action === 'decline' ? 'Rifiuta' : 'Invia Proposta'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoConsultManager;
